import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { SeverityLevel } from '@microsoft/applicationinsights-web';

import {
  PayrollDetailsFileViewModel,
  PayrollReportDeviationsFileViewModel,
  PayrollReportSummaryViewModel,
  ReportInformationFileViewModel,
} from '@fora-monorepo/employer-my-pages-common/types';
import { trackException, trackTrace } from '@fora-monorepo/webapp-analytics';

import { createThunkError } from '../utils';
import { envConfig } from '../../config';
import { mockPayrollReportCSV, mockPayrollReportDeviationsFile, mockPayrollReportSummary } from './mocks';
import { mockPayrollReportInfromationFile } from './mocks/mockPayrollReportInformationFile';

export const getPayrollReportSummary = createAsyncThunk(
  'getPayrollReportSummary',
  async (data: { partnerNumberOrganization: string; reportId: string }, thunkAPI) => {
    if (envConfig.mockDataFrontend) {
      await new Promise(resolve => setTimeout(resolve, 1000));
      return mockPayrollReportSummary;
    }

    return axios
      .get(`${envConfig.bffBaseUri}/payroll/report-summary/${data.partnerNumberOrganization}/${data.reportId}`)
      .then(res => {
        if (res.status === 200) {
          return res.data as PayrollReportSummaryViewModel;
        }
      })
      .catch(err => {
        trackException(err);
        return thunkAPI.rejectWithValue(createThunkError('Could not get pay report summary', err));
      });
  }
);

export const getReportInformationCSV = createAsyncThunk(
  'getReportInformationCSV',
  async (data: { partnerNumberOrganization: string; reportId: string }, thunkAPI) => {
    if (envConfig.mockDataFrontend) {
      await new Promise(resolve => setTimeout(resolve, 1500));
      return mockPayrollReportInfromationFile;
    } else {
      return axios
        .get(
          `${envConfig.bffBaseUri}/payroll/download/report-information/${data.partnerNumberOrganization}/${data.reportId}`
        )
        .then(res => {
          if (res.status === 200) {
            return res.data as ReportInformationFileViewModel;
          } else if (res.status === 204) {
            trackTrace(
              `report information csv for reportId: ${data.reportId} returned 204 no content`,
              SeverityLevel.Warning
            );
            return null;
          }
        })
        .catch(err => {
          trackException(err);
          return thunkAPI.rejectWithValue(
            createThunkError(`Could not get report information file for reportId: ${data.reportId}`, err)
          );
        });
    }
  }
);

export const getReportDeviationsCSV = createAsyncThunk(
  'getReportDeviationsCSV',
  async (data: { partnerNumberOrganization: string; reportId: string }, thunkAPI) => {
    if (envConfig.mockDataFrontend) {
      await new Promise(resolve => setTimeout(resolve, 1500));
      return mockPayrollReportDeviationsFile;
    } else {
      return axios
        .get(
          `${envConfig.bffBaseUri}/payroll/download/report-deviations/${data.partnerNumberOrganization}/${data.reportId}`
        )
        .then(res => {
          if (res.status === 200) {
            return res.data as PayrollReportDeviationsFileViewModel;
          } else if (res.status === 204) {
            trackTrace(`deviation csv for reportId: ${data.reportId} returned 204 no content`, SeverityLevel.Warning);
            return null;
          }
        })
        .catch(err => {
          trackException(err);
          return thunkAPI.rejectWithValue(
            createThunkError(`Could not get report deviations file for reportId: ${data.reportId}`, err)
          );
        });
    }
  }
);

export const getPayrollDetailsCSV = createAsyncThunk(
  'getPayrollDetailsCSV',
  async (data: { partnerNumberOrganization: string; reportId: string; reportedAtTimestamp: string }, thunkAPI) => {
    if (envConfig.mockDataFrontend) {
      await new Promise(resolve => setTimeout(resolve, 1000));
      return mockPayrollReportCSV;
    }

    return axios
      .get(
        `${envConfig.bffBaseUri}/payroll/download/details/history/${data.partnerNumberOrganization}/${data.reportId}`,
        {
          params: { reportedAtTimestamp: data.reportedAtTimestamp },
        }
      )
      .then(res => {
        if (res.status === 200) {
          return res.data as PayrollDetailsFileViewModel;
        } else if (res.status === 204) {
          trackTrace(`csv for reportId: ${data.reportId} returned 204 no content`, SeverityLevel.Warning);
          return null;
        }
      })
      .catch(err => {
        trackException(err);
        return thunkAPI.rejectWithValue(
          createThunkError(`Could not get pay report details csv for reportId: ${data.reportId}`, err)
        );
      });
  }
);
